import { ShowHide } from '@/components/showHide';
import Tooltip from '@/components/tooltip';
import { useCompaniesToExportBeneficiaries } from '@/modules/beneficiary/components/exportBeneficiariesModal/hook/useCompaniesToExportBeneficiaries';
import { ExportBeneficiarySearchParams } from '@/modules/beneficiary/entities/interfaces';
import { Company } from '@/modules/company/entities/interfaces/company';
import ICONS from '@/shared/entities/constants/icons';
import formatText from '@/utils/formatText';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, DataTable, Dialog, Icon, Loader, Text } from 'vkit/lib/components';
import { Grid, Row } from 'vkit/lib/context';
import useExportBeneficiriesModal from './hook/useExportBeneficiariesModal';

interface ExportBeneficiariesModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  filters: ExportBeneficiarySearchParams;
}

const ExportBeneficiariesModal: React.FC<ExportBeneficiariesModalProps> = ({
  isOpen,
  handleClose,
  filters,
}) => {
  const [tablePage, setTablePage] = useState(1);

  const {
    loading: downloading,
    errorMessage,
    success,
    disableCloseButton,
    getExportedData,
    resetAll,
  } = useExportBeneficiriesModal();

  const {
    loadingCompanyGroup,
    allCompanyIds,
    selectedCompanyIds,
    maxCompaniesToSelect,
    updateCompanyId,
    paginatedCompanies,
    countSelectedCompaniesMessage,
    setSelectedCompanyIds,
  } = useCompaniesToExportBeneficiaries();

  const canExport = useMemo(() => {
    return selectedCompanyIds.length > 0 && !downloading && !success && !errorMessage;
  }, [selectedCompanyIds.length, downloading, success, errorMessage]);

  const handleCloseModal = useCallback(() => {
    if (disableCloseButton) {
      return;
    }
    resetAll();
    handleClose(false);
  }, [disableCloseButton, handleClose, resetAll]);

  const exportData = useCallback(() => {
    if (selectedCompanyIds.length) {
      getExportedData({
        companyId: selectedCompanyIds,
        ...filters,
      });
    }
  }, [filters, getExportedData, selectedCompanyIds]);

  const companyCheckbox = ({ id }: Company) => (
    <Grid alignItems='center'>
      <Tooltip
        title={
          selectedCompanyIds.length >= maxCompaniesToSelect && !selectedCompanyIds.includes(id)
            ? `O número máximo de empresas selecionadas é ${maxCompaniesToSelect}`
            : undefined
        }>
        <Checkbox
          small
          disabled={
            downloading ||
            (selectedCompanyIds.length >= maxCompaniesToSelect && !selectedCompanyIds.includes(id))
          }
          checked={selectedCompanyIds.includes(id)}
          onChange={(checked: boolean) => {
            updateCompanyId(checked, id);
          }}
        />
      </Tooltip>
    </Grid>
  );

  useEffect(() => {
    setSelectedCompanyIds(allCompanyIds.slice(0, maxCompaniesToSelect));
  }, [allCompanyIds, maxCompaniesToSelect, setSelectedCompanyIds]);

  useEffect(() => {
    if (isOpen && allCompanyIds.length === 1) {
      exportData();
    }
  }, [allCompanyIds.length, exportData, isOpen]);

  return (
    <Dialog
      shadow
      outlined
      rounded
      open={isOpen}
      elevation={2}
      size={750}
      persistent
      title={
        <Text
          value='Exportar beneficiários'
          title
          size='larger'
          icon='people-outline'
          iconColor='colorDefault'
          color='colorDefault'
          iconSize={32}
          textAlign='left'
        />
      }
      body={
        <>
          <ShowHide visible={loadingCompanyGroup}>
            <Grid alignContent='center'>
              <Loader color='colorDefault' />
            </Grid>
          </ShowHide>

          <ShowHide
            transition='fade'
            visible={!downloading && !success && !errorMessage && !loadingCompanyGroup}>
            <Grid column gap={16} margin='0 24px 24px'>
              <Grid column gap={8}>
                <Text
                  title
                  color='colorDefault'
                  value='Selecione as empresas que deseja exportar beneficiários.'
                />
                <Text
                  value={`Você pode selecionar até <b>${maxCompaniesToSelect}</b> empresas ou exportar até <b>13500</b> beneficiários.`}
                  size='medium'
                />
              </Grid>
              <DataTable
                isLoading={false}
                columns={[
                  {
                    width: '30px',
                    name: (
                      <Grid alignItems='center'>
                        <Checkbox
                          small
                          disabled={downloading}
                          indeterminate={
                            selectedCompanyIds.length > 0 &&
                            selectedCompanyIds.length < allCompanyIds.length &&
                            selectedCompanyIds.length < maxCompaniesToSelect
                          }
                          checked={
                            selectedCompanyIds.length === allCompanyIds.length ||
                            selectedCompanyIds.length === maxCompaniesToSelect
                          }
                          onChange={(checked: boolean) => {
                            setSelectedCompanyIds(
                              checked ? allCompanyIds.slice(0, maxCompaniesToSelect) : [],
                            );
                          }}
                        />
                      </Grid>
                    ),
                    format: companyCheckbox,
                    path: 'id',
                  },
                  { name: 'Empresa', path: 'name' },
                  {
                    name: 'CNPJ',
                    format: ({ cnpj }: Company) => formatText(cnpj, 'cnpj'),
                    path: 'cnpj',
                    width: '200px',
                  },
                ]}
                data={paginatedCompanies[tablePage - 1]}
                showSelectPageSize={false}
                elevation={0}
                onChangePage={(currentPage: number) => setTablePage(currentPage)}
                page={tablePage}
                totalPages={paginatedCompanies.length}
                total={allCompanyIds.length}
              />
            </Grid>
          </ShowHide>

          <ShowHide transition='fade' visible={downloading}>
            <Row>
              <Grid alignContent='center'>
                <Loader color='colorDefault' />
              </Grid>
              <Text
                margin='12px 0'
                size='larger'
                textAlign='center'
                value='Processando sua solicitação'
              />
            </Row>
            <Row>
              <Text
                margin='12px 0'
                size='medium'
                textAlign='center'
                value='Aguarde alguns instantes, estamos processando sua solicitação e realizando a geração dos dados. Em alguns instantes o download solicitado será inicializado automaticamente.'
              />
            </Row>
          </ShowHide>

          <ShowHide transition='fade' visible={!downloading && success}>
            <Row>
              <Grid alignContent='center'>
                <Icon name='done-all-outline' color='success' size={48} />
              </Grid>
              <Text
                margin='12px 0'
                size='larger'
                textAlign='center'
                value='Solicitação realizada com sucesso!'
                color='success'
              />
            </Row>
            <Row>
              <Text
                margin='12px 0'
                size='medium'
                textAlign='center'
                value='Sua solicitação foi atendida com sucesso e o download do arquivo já foi iniciado.'
              />
            </Row>
          </ShowHide>

          <ShowHide transition='fade' visible={!downloading && errorMessage}>
            <Row>
              <Grid alignContent='center'>
                <Icon name='slash-outline' color='danger' size={48} />
              </Grid>
              <Text
                margin='12px 0'
                size='larger'
                textAlign='center'
                value='Ocorreu um erro e sua solicitação não foi atendida!'
                color='danger'
              />
            </Row>
            <Row>
              <Text margin='12px 0' size='medium' textAlign='center' value={String(errorMessage)} />

              <Grid alignContent='center' gap={16}>
                <Button
                  label='Tentar novamente'
                  size='medium'
                  color='danger'
                  outlined
                  solo
                  onClick={exportData}
                />

                <Button
                  label='Alterar seleção de empresas'
                  size='medium'
                  color='colorDefault'
                  outlined
                  solo
                  onClick={resetAll}
                />
              </Grid>
            </Row>
          </ShowHide>
        </>
      }
      footer={
        <Grid
          alignContent={allCompanyIds.length > 1 ? 'justify' : 'right'}
          alignItems='center'
          gap={16}>
          <Button
            disabled={disableCloseButton}
            label='Fechar'
            icon='close'
            iconColor='colorDefault'
            size='medium'
            color='colorDefault'
            outlined
            solo
            onClick={handleCloseModal}
          />

          {allCompanyIds.length > 1 && (
            <Grid alignContent='right' alignItems='center' gap={16}>
              {countSelectedCompaniesMessage}

              <Button
                disabled={!canExport}
                label='Exportar'
                icon={ICONS.DOWNLOAD}
                iconColor={canExport ? 'colorDefault' : 'light'}
                size='medium'
                color='colorDefault'
                onClick={exportData}
              />
            </Grid>
          )}
        </Grid>
      }
    />
  );
};

export default ExportBeneficiariesModal;
